import {
  InputSelectControlField,
  TextInputField
} from '@sequencehq/core-components'
import {
  CountriesAlpha2,
  getStateOptions
} from '@sequencehq/api/utils/commonEnums'
import { getPostcodeLabel } from '@sequencehq/utils'
import { useMemo } from 'react'
import { CountrySelectField } from 'SettingsV2/index'
import { CustomerFormFieldControls } from '../types'
import { fieldStyles, inputStyles } from '../sections/styles'

/** The billing and shipping addresses share a shape, so either fits here. */
export type CustomerAddressFieldControls = {
  country: CustomerFormFieldControls['address.country']
  line1: CustomerFormFieldControls['address.line1']
  line2: CustomerFormFieldControls['address.line2']
  town: CustomerFormFieldControls['address.town']
  state: CustomerFormFieldControls['address.state']
  postcode: CustomerFormFieldControls['address.postcode']
}

export const CustomerAddressFields = ({
  fields,
  isCountryClearable = false,
  'data-testid': testId
}: {
  fields: CustomerAddressFieldControls
  /** Shows a ✕ in the country select so a picked country can be cleared. */
  isCountryClearable?: boolean
  'data-testid'?: string
}) => {
  const selectedCountry = fields.country.value as CountriesAlpha2

  const addressStateOptions = useMemo(() => {
    if (!selectedCountry) return []
    return getStateOptions(selectedCountry)
  }, [selectedCountry])

  const showAddressState = addressStateOptions.length > 0

  return (
    <>
      <CountrySelectField
        data-testid={testId ? `${testId}.country` : undefined}
        value={fields.country.value}
        onChange={newCountryValue => fields.country.onChange(newCountryValue)}
        validationErrors={fields.country.validationErrors}
        onClear={
          isCountryClearable ? () => fields.country.onChange('') : undefined
        }
        styles={fieldStyles}
      />

      {Boolean(selectedCountry) && (
        <>
          <TextInputField
            data-testid={testId ? `${testId}.line1` : undefined}
            value={fields.line1.value}
            onChange={e => fields.line1.onChange(e.target.value)}
            placeholder="Address line 1"
            validationErrors={fields.line1.validationErrors}
            {...inputStyles}
            styles={fieldStyles}
          />

          <TextInputField
            data-testid={testId ? `${testId}.line2` : undefined}
            value={fields.line2.value}
            onChange={e => fields.line2.onChange(e.target.value)}
            placeholder="Address line 2 (optional)"
            validationErrors={[]}
            {...inputStyles}
            styles={fieldStyles}
          />

          <TextInputField
            data-testid={testId ? `${testId}.town` : undefined}
            value={fields.town.value}
            onChange={e => fields.town.onChange(e.target.value)}
            placeholder="City/town"
            validationErrors={fields.town.validationErrors}
            {...inputStyles}
            styles={fieldStyles}
          />

          {showAddressState && (
            <InputSelectControlField
              data-testid={testId ? `${testId}.state` : undefined}
              value={fields.state.value}
              onChange={newStateValue =>
                fields.state.onChange(newStateValue as never)
              }
              options={addressStateOptions}
              placeholder="Select state"
              validationErrors={fields.state.validationErrors}
              styles={fieldStyles}
            />
          )}

          <TextInputField
            data-testid={testId ? `${testId}.postcode` : undefined}
            value={fields.postcode.value}
            placeholder={getPostcodeLabel(fields.country.value)}
            onChange={e => fields.postcode.onChange(e.target.value)}
            validationErrors={fields.postcode.validationErrors}
            {...inputStyles}
            styles={fieldStyles}
          />
        </>
      )}
    </>
  )
}
